import React, { useContext, useEffect, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { cn, isThinPage, mapEdgesToNodes } from "../../lib/helpers";
import { pageDocPath, trackPath } from "../../../helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fas as iconSet } from "@fortawesome/free-solid-svg-icons";
import { TinyDownArrow } from "../icon/tiny-down-arrow";
import { Logo } from "../icon/logo";
import { SiteContext } from "./site-context";
import Container from "./container";
import { useHoverIntent } from "react-use-hoverintent";
import { TinyPills } from "../card/card";
import { CardContext } from "../card/context";
import { PublishDate } from "../card/publish-date";
import {
  ATTRIBUTION_PARAM,
  CollapsibleMenu,
  DEMO_PHRASE,
  DEMO_URL,
  LOGIN_PHRASE,
  LOGIN_URL,
} from "../sections/snowflake/navigation";
import { DARK_BACKGROUNDS } from "./layout";
import { Banner, CLICK_PREFIX } from "../sections/snowflake/banner";

const TOP_LEVEL_PATHS = [
  "/", "customers", "pricing", "pricing/zra", "integrations", "careers", "community", "plays",
  "resources", "security", "resources/signals", "prompts", "podcast", "events",
  "academy"
];
const OVERLAY_TYPES = [
  "offering", "account", "competitor", "story", "playbook", "signal", "prompt",
  "asset", "agent", "scenario", "trainingCourse", "invite"
];

export const DOCS_PATHS = ["/help/"];
const NON_STICKY_PATHS = ["/plays/"];

const CENTERED_LOGO_PATHS = ["/demo/", "/hero/", "/hello/lets-talk/", "/demo-brand/"];

// text or border color map from theme colors
export const colorMap = new Map([
  ["mint", "#098a44"],
  ["peach", "#D0411E"],
  ["lime", "#567F14"],
  ["robin", "#186EBE"],
  ["purple-default", "#723EC7"],
  ["default", "#888"],
  ["lemon", "#78770b"],
  ["melon", "#b72329"],
  ["secondary", "#FFFFFF"],
  ["auxiliary", "#111111"],
  ["dark-10", "#333333"],
  ["dark-20", "#555555"],
]);

const query = graphql`
  query GlobalNavQuery {
    menus: allSanityMenu(
      filter: { _id: { eq: "0ae244a8-0e78-482c-bb63-0422b316fcce" } }
    ) {
      edges {
        node {
          items {
            _key
            title
            description
            reference {
              ... on SanityPage {
                _id
                _type
                title
                slug {
                  current
                }
              } 
              ... on SanityOffering {
                _type
                title
                slug {
                  current
                }
              }
            }
            href
            icon
            bottomBorder
            children {
              _key
              title
              description
              reference {
                ... on SanityPage {
                  _id
                  _type
                  title
                  slug {
                    current
                  }
                } 
                ... on SanityOffering {
                  _type
                  title
                  slug {
                    current
                  }
                }
              }
              href
              icon
              bottomBorder
              color
              children {
                _key
                title
                description
                reference {
                  ... on SanityPage {
                    _id
                    _type
                    title
                    slug {
                      current
                    }
                  } 
                  ... on SanityOffering {
                    _type
                    title
                    slug {
                      current
                    }
                  }
                }
                href
                icon
                bottomBorder
              }
            }
          }
        }
      }
    }
    stories: allSanityStory(
      limit: 1
      sort: { fields: [_createdAt], order: DESC }
      filter: { status: { eq: "published" }, isFeatured: { eq: true } }
    ) {
      edges {
        node {
          ...cardNodeStory
        }
      }
    }
    posts: allSanityPost(
      limit: 1
      sort: { fields: [publishedAt], order: [DESC] }
      filter: { isPublished: { eq: true }, isFeatured: { eq: true } }
    ) {
      edges {
        node {
          ...cardNode
        }
      }
    }
  }
`;

const makeUrl = (item) =>
  item.href ? item.href : item.reference ? pageDocPath(item.reference) : null;

export const GlobalNav = () => {
  const site = useContext(SiteContext);
  const [isCollapsed, setCollapsed] = useState(true);

  const data = useStaticQuery(query) || {};
  const menus = mapEdgesToNodes(data.menus);
  const content = mapEdgesToNodes(data.stories).concat(
    mapEdgesToNodes(data.posts)
  );

  const path = pageDocPath(site.doc);
  const slimLayout = isThinPage(site);
  const isDocs =
    site?.doc._type === "documentation" || DOCS_PATHS.includes(path);
  const isLoginNavPage = site.isHomepage || isDocs;
  const [hasBanner, setHasBanner] = useState(false);
  useEffect(() => {
    setHasBanner(site.banners.some(
      (b) => !site.metrics.analytics?.storage?.getItem(`${CLICK_PREFIX}${b._id}`)
    ));
  }, [site.banners]);
  const nonSticky = site.hideNav || isDocs || NON_STICKY_PATHS.includes(path);

  const isOverlayPage = TOP_LEVEL_PATHS.includes(site?.doc?.slug?.current) || OVERLAY_TYPES.includes(site?.doc._type);
  const transparent = isOverlayPage && site.isPageTop;
  const darkBg = !site.isHomepage && DARK_BACKGROUNDS.includes(site?.doc?.sections?.[0]?.background)
    || site.isDarkBg && !site?.doc?.sections?.[0]?.background || site?.doc?.backgroundImage?.asset;
  const centeredLogo = site?.doc._type === "prospect" || CENTERED_LOGO_PATHS.includes(path);

  return (
    <>
      <div
        className={cn(
          "z-100 w-full text-sm top-0 transition-colors duration-200",
          site.hideNav ? null : transparent ? "bg-transparent" : "bg-white",
          // non-sticky
          !site.hideNav && !nonSticky
            ? isOverlayPage
              ? site.isPageTop
                ? hasBanner
                  ? "relative"
                  : "absolute"
                : hasBanner
                  ? "sticky"
                  : "sticky h-0"
              : "sticky"
            : "absolute"
        )}
        id="nav"
      >
        <Banner />
        <div
          className={cn(
            "relative",
            site.hideNav || transparent ? null : "border-b border-dark-10"
          )}
        >
          {/* faux background element so menu height can be zero for smooth scroll */}
          {!site.hideNav && (
            <div
              className={cn(
                "absolute -top-px right-0 bottom-0 left-0 transition-colors duration-200",
                transparent ? "bg-transparent" : "bg-white"
              )}
            ></div>
          )}
          <Container
            maxWidth={slimLayout ? "max-w-screen-lg" : "max-w-screen-xl"}
            className={cn(
              "flex items-center mx-auto w-full relative p-3",
              site.hideNav && centeredLogo ? "justify-center" : "justify-between")}
          >
            <a href="/" className={transparent && darkBg ? "text-white" : null} role="link" aria-label="Home">
              {site.hideNav ? (
                <Logo
                  className={cn("h-8", darkBg ? "text-white" : null)}
                  isDarkBg={darkBg}
                />
              ) : (
                <>
                  <Logo
                    iconOnly={true}
                    className="hidden md:block lg:hidden h-6"
                  />
                  <Logo className="h-8 md:hidden lg:block" isDarkBg={darkBg} />
                </>
              )}
            </a>
            {!site.hideNav ? (
              <ul className="hidden md:flex items-center z-100 relative">
                {menus[0]?.items?.map((parent) => (
                  <MenuItem
                    key={parent._key}
                    menu={parent}
                    content={content}
                    transparent={transparent}
                    darkBg={darkBg}
                  />
                ))}
              </ul>
            ) : null}
            <div className={cn(
              "text-secondary text-sm",
              site.hideNav ? "flex" : "hidden md:flex"
            )}>
              <LoginMenu
                isLoginNavPage={isLoginNavPage}
                nonSticky={nonSticky}
                transparent={transparent}
                darkBg={darkBg}
              />
            </div>
            {!site.hideNav && (
              <>
                <CollapsibleMenu
                  className="md:hidden ml-2"
                  isMainNav={true}
                  isCollapsed={isCollapsed}
                  setCollapsed={setCollapsed}
                />
                <div className="absolute" style={{ zIndex: 999 }}>
                  {isCollapsed}
                </div>
              </>
            )}
          </Container>
        </div>
      </div>
    </>
  );
};

const MenuItem = ({ menu, content, transparent, darkBg }) => {
  const url = makeUrl(menu);
  const [isItemHover, itemRef] = useHoverIntent({ timeout: 150 });

  return (
    <li key={menu._key} ref={itemRef}>
      {url ? (
        <a
          href={url}
          className={cn(
            "flex items-center px-3 py-2 font-medium hover:bg-dark-5 rounded-lg cursor-pointer",
            transparent && darkBg ? "text-white hover:bg-light-20" : null,
            darkBg ? "hover:bg-dark-50" : null
          )}
        >
          {menu.title || menu.reference?.title}
        </a>
      ) : (
        <div
          className={cn(
            "flex items-center px-3 py-2 font-medium rounded-lg cursor-pointer",
            transparent && darkBg ? "text-white" : null,
            darkBg ? "hover:bg-dark-50" : "hover:bg-dark-5"
          )}
        >
          {menu.title || menu.reference?.title}
          {menu.children.length > 0 && (
            <TinyDownArrow
              className={cn(
                "ml-1",
                transparent && darkBg ? "text-secondary" : "text-auxiliary"
              )}
            />
          )}
        </div>
      )}
      {
        menu.children.length > 0 && (
          <div
            className={cn(
              "absolute tranform transition-opacity duration-200",
              isItemHover ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
          >
            <div className="pt-7 relative">
              <div
                className="absolute top-5 left-10 transform rotate-45 rounded-sm bg-white h-5 w-5"
                style={{ boxShadow: "-1px -1px 6px -1px rgba(0,0,0,0.1)" }}
              ></div>
              <PopoverMenu
                menu={menu}
                hasSubMenus={menu.children[0].children.length > 0}
                showContent={
                  menu.title === "Resources" ||
                  menu.reference?.title === "Resources"
                }
                content={content}
              />
            </div>
          </div>
        )
      }
    </li >
  );
};

const PopoverMenu = ({ menu, hasSubMenus, showContent, content }) => (
  <div
    className={cn("relative bg-white rounded-xl", showContent ? "flex" : null)}
    style={{ boxShadow: "0 16px 24px rgba(0,0,0,0.12)" }}
  >
    <ul
      className={cn(
        hasSubMenus ? "flex flex-row gap-4" : "p-3",
      )}
      style={hasSubMenus ? { minWidth: "540px", maxHeight: "490px" } : null}
    >
      {menu.children.map((child, index) => {
        const childUrl = makeUrl(child);
        return (
          <React.Fragment key={child._key}>
            <li
              className={
                cn(
                  "relative",
                  hasSubMenus ? "p-3" : null,
                  index === 1 ? "bg-dark-2" : null
                )}
              style={
                hasSubMenus ? { width: "calc(50% - 8px)" } : null
              }
            >
              <a
                href={childUrl}
                className={cn(
                  "p-2 items-center rounded-lg group whitespace-nowrap",
                  childUrl ? "hover:bg-dark-2" : null,
                  hasSubMenus ? `bg-${child.color} text-white rounded-full inline-flex mb-2 pr-4` : "flex pr-20 hover:text-link inline-block",
                )}
              >
                {iconSet[child.icon] ? (
                  <div
                    className={cn(
                      "mr-3",
                      hasSubMenus
                        ? cn("p-2 rounded-md", `bg-${child.color || "dark-10"}`)
                        : null
                    )}
                  >
                    <div className="relative h-5 w-5 flex justify-center items-center">
                      <FontAwesomeIcon
                        icon={iconSet[child.icon]}
                        className={
                          hasSubMenus
                            ? "text-secondary h-3.5"
                            : "group-hover:text-link text-dark-40"
                        }
                        style={
                          hasSubMenus
                            ? {
                              color:
                                colorMap.get(child.color) ||
                                colorMap.get("default"),
                            }
                            : null
                        }
                      />
                    </div>
                  </div>
                ) : <div className="mr-2"></div>}
                <div>
                  <p className="font-semibold">
                    {child.title || child.reference.title}
                  </p>
                  {child.description && (
                    <p className="mt-1 text-sm hidden md:block text-dark-60 group-hover:text-dark-70 whitespace-nowrap">
                      {child.description}
                    </p>
                  )}
                </div>

              </a>
              <SubMenu menu={child} />
            </li>
            {child.bottomBorder ? (
              <li className="border-b border-dark-5 w-full"></li>
            ) : null}
          </React.Fragment>
        );
      })}
    </ul>
    {showContent && <FeaturedContent content={content} />}
  </div>
);

const SubMenu = ({ menu, className, style }) =>
  menu.children.length > 0 && (
    <ul className={className} style={style}>
      {menu.children.map((child) => {
        const childUrl = makeUrl(child);
        return (
          <li key={child._key}>
            <a
              href={childUrl}
              className={cn(
                "inline-block group rounded-lg",
                child.description ? "bg-light-80 p-3 mt-3" : "p-2",
                iconSet[child.icon] ? "flex items-center" : null,
                childUrl ? "hover:text-link hover:bg-dark-2" : null,
              )}
            >
              {iconSet[child.icon] && (
                <span className="w-6 mr-1 flex justify-center">
                  <FontAwesomeIcon
                    icon={iconSet[child.icon]}
                    className="h-3 text-secondary group-hover:text-link"
                  />
                </span>
              )}
              <span className="flex-grow font-medium group-hover:text-link">
                {child.title || child.reference.title}
              </span>
              {child.description && (
                <div className="mt-2 text-xs text-dark-60">
                  {child.description}
                  <p className="mt-1 font-medium group-hover:text-link">
                    How they work together →
                  </p>
                </div>
              )}
            </a>
          </li>
        );
      })}
    </ul>
  );

const LoginMenu = ({ isLoginNavPage, nonSticky, transparent, darkBg }) => {
  const site = useContext(SiteContext);
  const path = trackPath(site?.doc);
  const matchPath = pageDocPath(site?.doc);
  const isCentered = ["prospect", "competitor"].includes(site?.doc._type)
    || CENTERED_LOGO_PATHS.includes(matchPath);

  const menu = []
    .concat([
      {
        title: LOGIN_PHRASE,
        href: LOGIN_URL,
        className: cn(
          "border",
          transparent
            ? "border-black text-black hover:bg-light-20 hover:border-white"
            : "border-black hover:bg-dark-5 hover:text-black"
        ),
        hide: true,
      },
    ])
    .concat(!isCentered ? [
      {
        title: DEMO_PHRASE,
        href: `${DEMO_URL}?${ATTRIBUTION_PARAM}=navigation--${path}`,
        className: cn(
          "border",
          transparent
            ? darkBg ? "bg-white border-white text-primary hover:bg-light-80" : "bg-primary text-white hover:bg-dark-80"
            : "bg-primary border-black text-white hover:bg-dark-80",
          "border-black transform transition-card scale-100 hover:scale-102 duration-200 ease-in-out",
          site?.isHomepage && transparent ? "hidden" : null
        ),
        onClick: () => {
          site.metrics.logEvent({
            label: DEMO_PHRASE.toLowerCase(),
            category: "button_click",
            action: "nav-contact",
          });
        },
      },
    ] : []);

  return (
    <ul
      className="flex items-center justify-end font-medium gap-2"
      style={!isCentered ? { minWidth: "13rem" } : null}
    >
      {menu.map((item, i) => {
        // so many scenarios
        const showLink =
          (nonSticky &&
            ((!site.hideNav && !item.reveal) ||
              (site.hideNav && !item.hide))) ||
          (!nonSticky &&
            ((!isLoginNavPage && !item.hide) ||
              (isLoginNavPage && site.isPageTop && !item.reveal) ||
              (isLoginNavPage && !site.isPageTop && !item.hide)));

        return showLink ? (
          <li key={i} className="flex justify-end">
            <a
              href={item.href}
              className={cn(
                "inline-block px-3 py-2 font-medium rounded-lg",
                site.hideNav
                  ? darkBg
                    ? "text-white hover:bg-primary hover:text-green-emerald"
                    : "text-green-emerald hover:bg-dark-5 hover:text-black"
                  : item.className
              )}
              onClick={item.onClick}
            >
              {item.title}
            </a>
          </li>
        ) : null;
      })}
    </ul>
  );
};

const FeaturedContent = ({ content }) => (
  <div className="bg-dark-2 border-l border-dark-10 py-5 px-4 rounded-r-xl w-64">
    <p className="text-auxiliary font-medium">Featured</p>
    {content.map((n) => (
      <CardContext.Provider value={n} key={n._id}>
        <a
          href={pageDocPath(n)}
          className="block border border-dark-10 bg-white p-3 rounded-lg mt-4 hover:text-black hover:bg-light-2 hover:border-link"
          key={n._id}
        >
          <TinyPills list={[n._type]} />
          <p className="my-1 font-medium leading-normal">{n.title}</p>
          <p className="text-auxiliary text-xs font-medium">
            <PublishDate />
          </p>
        </a>
      </CardContext.Provider>
    ))}
  </div>
);
